import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  APP_EMAIL_SENDER_ROLES,
  canPortal,
  CLIENT_REPORT_KINDS,
  replacedForClientBy,
  reportLabel,
  reportsPageUrl,
  staffReportLabel,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { EmailDialog } from '../email/EmailDialog.tsx';
import { PublishDialog } from './PublishDialog.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import {
  attachPdf,
  loadPublishedReports,
  loadStaffReports,
  setPublished,
  type PortalProject,
  type PortalReport,
} from './api.ts';
import { TableWrap } from '../components/TableWrap.tsx';
import { loadBreachReportRows, type BreachReportRow } from '../deliverables/breachReportApi.ts';
import { documentUrl, openInNewTab } from '../document-viewer/open.ts';

export type ReportsMode = 'client' | 'staff';

export interface ReportsListProps {
  readonly sb: SupabaseClient;
  readonly mode: ReportsMode;
  readonly projects: readonly PortalProject[];
  /** Staff roles (decides whether attach and publish controls show; the database decides what is allowed). */
  readonly roles?: readonly string[];
  /** Called after a PDF is opened (Preview as client audits it). */
  readonly onOpened?: (r: PortalReport) => void;
  /** Open an address in a new tab (tests record it). */
  readonly openTab?: (url: string) => void;
}

const dateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const sizeText = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} kB`;

/** Reports of the given projects: published ones for clients; every live report with publish controls for staff. */
export function ReportsList({
  sb,
  mode,
  projects,
  roles = [],
  onOpened,
  openTab = openInNewTab,
}: ReportsListProps): ReactElement {
  const [reports, setReports] = useState<PortalReport[] | null>(null);
  const [breachReports, setBreachReports] = useState<readonly BreachReportRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const ids = projects.map((p) => p.id).join(',');
  const orgs = projects.map((p) => p.clientOrgId).join(',');
  const canPublish = mode === 'staff' && canPortal(roles, 'report.publish');
  // F14: every staff role emails a report's link to people Teslyn knows (decision 508)
  const canEmail = mode === 'staff' && roles.some((r) => APP_EMAIL_SENDER_ROLES.includes(r));
  const [publishing, setPublishing] = useState<{
    readonly report: PortalReport;
    readonly project: PortalProject;
  } | null>(null);
  const [emailing, setEmailing] = useState<{
    readonly report: PortalReport;
    readonly project: PortalProject;
  } | null>(null);

  const reload = useCallback(() => {
    setError(null);
    const list = ids ? ids.split(',') : [];
    (mode === 'client' ? loadPublishedReports(sb, list) : loadStaffReports(sb, list))
      .then((r) => setReports(mode === 'client' ? r.filter((x) => CLIENT_REPORT_KINDS.includes(x.kind)) : r))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    // R234: beside the published reports, not one of them; a failure here leaves the row out and the list stands
    loadBreachReportRows(
      sb,
      list.map((id, i) => ({ id, clientOrgId: orgs.split(',')[i] || null })),
      mode,
    )
      .then(setBreachReports)
      .catch(() => setBreachReports([]));
  }, [sb, mode, ids, orgs]);
  useEffect(reload, [reload]);

  async function run(id: string, action: () => Promise<void>, done: string): Promise<void> {
    setBusy(id);
    try {
      await action();
      setNotice({ ok: true, text: done });
    } catch (e: unknown) {
      setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
    reload();
  }

  function open(r: PortalReport): void {
    if (!r.publication) return;
    openTab(documentUrl({ doc: 'report', id: r.id }));
    onOpened?.(r);
  }

  const projectOf = (id: string): PortalProject | undefined => projects.find((p) => p.id === id);
  const many = projects.length > 1;
  // one project (the Reports page's choice, R198, or a project's Reports tab): the empty list names it
  const only = projects.length === 1 ? projects[0]! : null;

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!reports) return <Loading what="reports" />;
  return (
    <div className="stack">
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {reports.length === 0 && breachReports.length === 0 ? (
        <div className="panel">
          <p className="body">
            {mode === 'client'
              ? `No reports have been published${only ? ` for ${only.number}` : ''} yet. Daily and weekly reports appear here when your survey company publishes them.`
              : `No reports${only ? ` for ${only.number}` : ''} yet. Reports appear here when Teslyn Field makes them and syncs; Teslyn Field uploads each daily and weekly PDF after its report syncs.`}
          </p>
        </div>
      ) : (
        <TableWrap>
          <table className="table">
            <thead>
              <tr>
                {many && <th className="label">Project</th>}
                <th className="label">Report</th>
                <th className="label">{mode === 'client' ? 'Published' : 'Made'}</th>
                {mode === 'staff' && <th className="label">Client portal</th>}
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {breachReports.map((b) => (
                <tr key={`breach:${b.projectId}`}>
                  {many && <td className="data">{projectOf(b.projectId)?.number ?? ''}</td>}
                  <td className="reports__file">{b.file_name}</td>
                  <td className="caption">{dateTime(b.uploaded_at)}</td>
                  {mode === 'staff' && (
                    <td>
                      <BreachChip approved={b.approved} />
                    </td>
                  )}
                  <td className="table__actions">
                    <div className="row row--end">
                      <Button
                        variant="quiet"
                        aria-label={`Open PDF of ${b.file_name}`}
                        onClick={() => openTab(documentUrl({ doc: 'breach-report', project: b.projectId }))}
                      >
                        Open PDF
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {reports.map((r) => {
                // R139: the client reads no revision number; staff see which revision they publish
                const label = mode === 'client' ? reportLabel(r) : staffReportLabel(r);
                const pub = r.publication;
                const project = projectOf(r.projectId);
                return (
                  <tr key={r.id}>
                    {many && <td className="data">{project?.number ?? ''}</td>}
                    <td>{label}</td>
                    <td className="caption">
                      {mode === 'client'
                        ? pub?.publishedAt
                          ? dateTime(pub.publishedAt)
                          : ''
                        : dateTime(r.generatedAt)}
                    </td>
                    {mode === 'staff' && (
                      <td>
                        <StatusChip report={r} reports={reports} />
                      </td>
                    )}
                    <td className="table__actions">
                      <div className="row row--end">
                        {pub && (
                          <Button variant="quiet" aria-label={`Open PDF of ${label}`} onClick={() => open(r)}>
                            Open PDF
                          </Button>
                        )}
                        {canEmail && pub && project && (
                          <Button
                            variant="quiet"
                            aria-label={`Email ${label}`}
                            onClick={() => {
                              setNotice(null);
                              setEmailing({ report: r, project });
                            }}
                          >
                            Email…
                          </Button>
                        )}
                        {canPublish && project && (
                          <>
                            <AttachButton
                              label={label}
                              replacing={pub !== null}
                              busy={busy === r.id}
                              onFile={(file) =>
                                void run(
                                  r.id,
                                  () => attachPdf(sb, project, r.id, file),
                                  `${file.name} attached to ${label}.`,
                                )
                              }
                            />
                            {pub && !pub.published && project.clientOrgId && (
                              <Button
                                aria-label={`Publish ${label}`}
                                busy={busy === r.id}
                                onClick={() => {
                                  // decisions 883, 884: the email's words and contents are chosen with Publish
                                  setNotice(null);
                                  setPublishing({ report: r, project });
                                }}
                              >
                                Publish…
                              </Button>
                            )}
                            {pub?.published && (
                              <Button
                                aria-label={`Unpublish ${label}`}
                                busy={busy === r.id}
                                onClick={() =>
                                  void run(
                                    r.id,
                                    async () => {
                                      await setPublished(sb, r.id, false);
                                    },
                                    `${label} is no longer published.`,
                                  )
                                }
                              >
                                Unpublish
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}
      {publishing && (
        <PublishDialog
          sb={sb}
          report={publishing.report}
          project={publishing.project}
          reports={reports}
          onClose={(words) => {
            setPublishing(null);
            if (words) {
              setNotice({ ok: true, text: words });
              reload();
            }
          }}
        />
      )}
      {emailing && (
        <EmailDialog
          sb={sb}
          tenantId={emailing.project.tenantId}
          projectId={emailing.project.id}
          clientOrgId={emailing.project.clientOrgId}
          subject={`${emailing.project.number} ${emailing.project.title}: ${reportLabel(emailing.report)}`}
          body={`The ${reportLabel(emailing.report)} of ${emailing.project.number} ${emailing.project.title} is in Teslyn Cloud${
            // R198: the link opens Reports on this project
            reportsPageUrl(window.location.origin, emailing.project.id)
              ? `: ${reportsPageUrl(window.location.origin, emailing.project.id)!}`
              : ', Reports'
          }.`}
          onClose={(words) => {
            setEmailing(null);
            if (words) setNotice({ ok: true, text: words });
          }}
        />
      )}
      {canPublish && projects.some((p) => !p.clientOrgId) && (
        <p className="caption muted">
          Link a client organisation to {projects.length === 1 ? 'this project' : 'a project'} before
          publishing its reports.
        </p>
      )}
    </div>
  );
}

const withPublished = (r: PortalReport) => ({ ...r, published: r.publication?.published ?? null });

function StatusChip({
  report,
  reports,
}: {
  readonly report: PortalReport;
  readonly reports: readonly PortalReport[];
}): ReactElement {
  const pub = report.publication;
  if (!pub) return <span className="caption muted">No PDF attached</span>;
  const replacedBy = pub.published
    ? null
    : replacedForClientBy(withPublished(report), reports.map(withPublished));
  if (replacedBy !== null)
    return (
      <span className="chip chip--role caption">{`Not published · replaced for the client by revision ${replacedBy}`}</span>
    );
  return pub.published ? (
    <span className="chip chip--pass caption">Published</span>
  ) : (
    <span className="chip chip--role caption">
      {`Not published · ${sizeText(pub.sizeBytes)} · ${
        pub.source === 'field' ? 'from Teslyn Field' : 'attached in Teslyn Cloud'
      }`}
    </span>
  );
}

/** Whether the client has the breach report now (decision 1034): nothing to publish, it follows the register. */
function BreachChip({ approved }: { readonly approved: boolean | null }): ReactElement {
  if (approved) return <span className="chip chip--pass caption">Client sees it: a breach is approved</span>;
  return (
    <span className="chip chip--role caption">
      {approved === null ? 'Client sees it while a breach is approved' : 'Not shown: no breach approved'}
    </span>
  );
}

function AttachButton({
  label,
  replacing,
  busy,
  onFile,
}: {
  readonly label: string;
  readonly replacing: boolean;
  readonly busy: boolean;
  readonly onFile: (f: File) => void;
}): ReactElement {
  const input = useRef<HTMLInputElement>(null);
  const text = replacing ? 'Replace PDF' : 'Attach PDF';
  return (
    <>
      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf"
        className="sr-only"
        tabIndex={-1}
        aria-label={`PDF file for ${label}`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) onFile(f);
        }}
      />
      <Button
        variant="quiet"
        aria-label={`${text} for ${label}`}
        busy={busy}
        onClick={() => input.current?.click()}
      >
        {text}
      </Button>
    </>
  );
}
